import { clientInstance } from '../utils/http-client'
import { unifiedActionOverride } from './common_table_actions'
import { UnifiedTargetType } from './opportunities/targetTypeMap'

/**
 * Remaining hand-written export helpers pending codegen migration (FIR-6872
 * follow-up): the unified opportunities export (client-side param reshaping)
 * and the tradespend blob exports (sync blob responses in the tradespend app).
 * Everything else moved to `services/downloads` / `services/samples`.
 */

// ─── Target Table Exports V2 (async pattern via download widget) ─────────────

/**
 * Generic request body for starting a V2 target table export.
 */
export interface TargetExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Generic response from starting a V2 target table export.
 */
export interface TargetExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 chain CRM reports export.
 */
export async function startChainReportsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/reports/chains/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 door CRM reports export.
 */
export async function startDoorReportsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/reports/doors/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 export through the unified `/opportunities/` engine (FIR-7822).
 *
 * Accepts the same {@link TargetExportV2Request} the export modals already build,
 * then re-shapes it to the unified bulk-action contract so a select-all export
 * re-resolves the SAME id set the unified list rendered: the table filters go on
 * the query string (adapted + `target_type`), while `include_ids` / `exclude_ids`
 * move to the body top-level. The response is the same 202 `request_id`, so the
 * existing download-widget polling is unchanged. `unifiedDoors` selects the
 * doors-scoped endpoint.
 */
export async function startUnifiedTargetExportV2(
  data: TargetExportV2Request,
  unifiedTargetType: UnifiedTargetType,
  unifiedDoors: boolean
): Promise<TargetExportV2Response> {
  const { include_ids, exclude_ids, ...filters } = (data.filter_params ??
    {}) as Record<string, unknown>
  // Reuse the shared unified-action endpoint/param builder so URL + adapted-param
  // construction stays in lockstep with the other bulk actions. Non-null here
  // because `unifiedTargetType` is always provided on the unified export path.
  const { url, params } = unifiedActionOverride(
    'bulk/export/',
    filters,
    unifiedTargetType,
    unifiedDoors
  )!
  const response = await clientInstance.post<TargetExportV2Response>(
    url,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
      include_ids: include_ids ?? [],
      exclude_ids: exclude_ids ?? [],
      sort: data.sort,
    },
    { params }
  )
  return response.data
}

// ─── Rebate Export Functions (sync blob pattern via download widget) ──────────

/**
 * Common request body for rebate table exports.
 */
export interface RebateExportRequest {
  selected_columns: string[]
  export_type: string
}

/**
 * Export rebate enrollments for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportRebateEnrollments(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/enrollments/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Rebate Enrollments - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export attribution codes for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportAttributionCodes(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/attribution-codes/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Attribution Codes - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export manufacturer invoices.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportManufacturerInvoices(
  data: RebateExportRequest,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/invoices/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Invoices - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}
